// Pure logic shared by the two document panels. Deterministic so the filtering, grouping and
// mutation payloads can be unit tested without mounting a panel or touching Apollo.

import { CUSTOM_DOCUMENT_TYPES, DOCUMENT_GROUPS, DOCUMENT_TYPES } from './constants';

/**
 * Rows this panel is responsible for. `archived` is the only thing separating the two panels:
 * CustomDocuments shows the un-archived working set, ArchiveDocuments the filed one. The old
 * code tested `!doc.archived` and `!!doc.archived`, so a null archived counts as un-archived.
 */
export const documentsFor = (documents, archived) =>
    (documents || []).filter((doc) => (archived ? !!doc.archived : !doc.archived));

/** Upload dropdown options. `allowedTypes` narrows them further, as the old prop did. */
export const typeOptionsFor = ({ custom, allowedTypes }) => {
    const base = custom ? DOCUMENT_TYPES.filter((d) => CUSTOM_DOCUMENT_TYPES.includes(d.value)) : DOCUMENT_TYPES;
    return allowedTypes ? base.filter((d) => allowedTypes.includes(d.value)) : base;
};

/**
 * Documents bucketed into the display groups, empty groups dropped.
 *
 * Every input row appears in exactly one output group: anything whose type is not claimed by a
 * named group falls through to OTHER, so the grouped view can never show fewer documents than
 * the flat list did.
 */
export const groupDocuments = (documents) => {
    const buckets = Object.fromEntries(DOCUMENT_GROUPS.map((g) => [g.key, []]));

    (documents || []).forEach((doc) => {
        const group = DOCUMENT_GROUPS.find((g) => (g.types || []).includes(doc.type));
        buckets[group ? group.key : 'OTHER'].push(doc);
    });

    return DOCUMENT_GROUPS.reduce(
        (acc, g) => (buckets[g.key].length ? [...acc, { ...g, documents: buckets[g.key] }] : acc),
        []
    );
};

/**
 * Draft after a file lands. The name is auto-filled from the filename only when the user has
 * not typed one, matching the old behaviour — retyping then re-dropping must not clobber it.
 */
export const draftWithFile = (draft, { link, fileName }) => ({
    ...draft,
    link,
    ...(draft?.name ? {} : { name: fileName }),
});

/**
 * Insert payload. `archived` is sent only for the archive panel — the old CustomDocuments
 * omitted the key entirely rather than sending false, and the column's default handles it.
 */
export const insertPayload = ({ clientId, draft, archived }) => ({
    client_id: clientId,
    link: draft.link,
    name: draft.name,
    type: draft.type,
    ...(archived ? { archived: true } : {}),
});
